import React from 'react';
import type { VocabularyGroup } from '../../types/vocabulary.types';
import { Button } from '../common/Button';

interface DeleteGroupConfirmModalProps {
  isOpen: boolean;
  group: VocabularyGroup | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
}

export const DeleteGroupConfirmModal: React.FC<DeleteGroupConfirmModalProps> = ({
  isOpen,
  group,
  onClose,
  onConfirm,
  isDeleting,
}) => {
  if (!isOpen || !group) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
      <div
        className="relative w-full max-w-md glass-card border-white/10 p-6 rounded-2xl shadow-2xl bg-surface-950/95 border text-slate-100 animate-scale-up"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-group-modal-title"
      >
        {/* Warning Icon & Header */}
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-400 shrink-0">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="delete-group-modal-title" className="text-lg font-bold text-white tracking-tight">
              Delete Group "{group.name}"?
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Are you sure you want to delete this collection?
            </p>
          </div>
        </div>

        {/* Safety Note Callout */}
        <div className="mt-4 p-3.5 rounded-xl bg-surface-900/80 border border-white/10 space-y-1.5 text-xs text-slate-300">
          <div className="flex items-center gap-1.5 font-semibold text-emerald-400">
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>Your vocabulary is completely safe:</span>
          </div>
          <ul className="list-disc list-inside space-y-1 text-slate-400 pl-1">
            <li>Words in this group will <strong className="text-slate-200">NOT</strong> be deleted.</li>
            <li>Flashcard mastery and spaced-repetition schedules are preserved.</li>
            <li>Only this group category and its associations will be removed.</li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-5 border-t border-white/10 mt-5">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            id="confirm-delete-group-btn"
            type="button"
            variant="danger"
            loading={isDeleting}
            disabled={isDeleting}
            onClick={() => void onConfirm()}
          >
            Delete Group
          </Button>
        </div>
      </div>
    </div>
  );
};
